import { api } from "./api";
import qs from "qs";

type BoardRole = "READ";
// type BoardRole = "WRITE" | "READ" | "DELETE";

interface AssignBoardRoleParams {
    boardId: number;
    userId: number;
    boardRoles: BoardRole[];
}
  /**
 * 사용자 별 게시판 사용 권한 설정
 *
 * @param {AssignBoardRoleParams} params
 * @returns resultCode, message, data
 */
  export const assignBoardRoles = async (params: AssignBoardRoleParams): Promise<any> => {
    try {
      // payload - null 처리
      const res = await api().post("/admin/boards/assign-roles", null, {
        params,
        paramsSerializer: (params: any) => qs.stringify(params, { arrayFormat: 'repeat' }) // 권한 배열 쪼게기
      });
      return res;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  /**
   * 사용자 목록 조회
   * 
   * @returns resultCode, message, data
   */  
  export const getAllUsers = async () => {
    try {
      const res = await api().get("/admin/user/list");
      return res?.data;
    } catch (e) {
      console.log(e);
      return "false";
    }
  };
  
  /**
   * 게시판 목록 조회
   * 
   * @returns resultCode, message, data
   */
  export const getAllBoards = async () => {
    try {
      const res = await api().get("/admin/boards");
      return res?.data;
    } catch (e) {
      console.log(e);
      return "false";
    }
  };

  /**
   * 게시판 별 역할 목록 조회
   *
   * @param {number} boardId
   * @returns resultCode, message, data
   */
  export const getBoardPerRoles = async ({boardId} : {boardId: number}) => {
    try {
      if(!boardId) return;

      const res = await api().get("/admin/boards/roles", {
        params: { boardId }
      });
      return res?.data;
    } catch (e) {
      console.log(e);
      return "false";
    }
  };

  export const getPendingUsers = async() => {
    try {
      const { data } = await api().get("/admin/pending-users");
      return data;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  export const approveUser = async(userId: number, role: string) => {
    try {
      const params = { role };
      const { data } = await api().post(`/admin/user/${userId}/approve`, null, { params });
      return data;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  export const rejectUser = async(userId: number) => {
    try {
      const { data } = await api().post(`/admin/user/${userId}/reject`);
      return data;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  export const editUserName = async(userId: number, newName: string) => {
    try {
      const body = { userId, name: newName };
      const { data } = await api().post("/admin/user/username", body);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  export const editUserPassword = async(userId: number, newPassword: string) => {
    try {
      const body = { userId, newPassword };
      const { data } = await api().post("/admin/user/password", body);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  export interface UpdateUserPayload {
    userId: number;
    englishName?: string;
    role: string;
    email?: string;
    birthDate?: string;        // 'YYYY-MM-DD'
    phone?: string;
    workplace?: string;
    medicalLicenseNumber?: string;
    department?: string;
    position?: string;
    isSpecialist?: boolean; // false 면 서버에서 전문의 번호도 지운다
    specialistNumber?: string;
  }
  
  export interface ApiEnvelope<T = unknown> {
    resultCode: string;
    message: string;
    data: T;
    total: number;
  }
  
  export interface AdminApiResult<T = unknown> {
    success: boolean;
    status: number;
    code: string;
    message: string;
    data?: T;
  }

  export const createUser = async(username: string, password: string, name: string) => {
    try {
      const body = { username, password, name };
      const { data } = await api().post("/admin/user", body);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  export const changeUserRole = async (
    payload: UpdateUserPayload,
  ): Promise<AdminApiResult> => {
    try {
      const cleaned: Record<string, unknown> = { userId: payload.userId, role: payload.role };
      (['englishName', 'email', 'birthDate', 'phone', 'workplace', 'medicalLicenseNumber',
        'department', 'position', 'isSpecialist', 'specialistNumber'] as const).forEach(k => {
        const v = payload[k];
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          cleaned[k] = v;
        }
      });
  
      const { data } = await api().post<ApiEnvelope>('/admin/user/role', cleaned);
  
      return {
        success: true,
        status: 200,
        code: data?.resultCode ?? 'SUCCESS',
        message: data?.message ?? '사용자 정보가 변경되었습니다.',
        data: data?.data,
      };
    } catch (error: any) {
      const status: number = error?.response?.status ?? 0;
      const serverData = error?.response?.data;
  
      if (status === 400) {
        return {
          success: false,
          status,
          code: serverData?.resultCode ?? 'BAD_REQUEST',
          message: serverData?.message ?? '입력 정보를 확인해주세요.',
        };
      }
      if (status === 403) {
        return {
          success: false,
          status,
          code: serverData?.resultCode ?? 'FORBIDDEN',
          message: serverData?.message ?? '권한이 없습니다.',
        };
      }
      if (status === 404) {
        return {
          success: false,
          status,
          code: serverData?.resultCode ?? 'USER_NOT_FOUND',
          message: serverData?.message ?? '사용자를 찾을 수 없습니다.',
        };
      }
      return {
        success: false,
        status,
        code: serverData?.resultCode ?? 'UNKNOWN',
        message:
          serverData?.message ??
          '사용자 정보 변경 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
      };
    }
  };

  export const withdrawUser = async(userId: number) => {
    try {
      const body = { userId };
      const { data } = await api().post("/admin/user/withdraw", body);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  // 사용자별 하위 카테고리 권한 조회
  export const getCategoryPermissions = async (userId: number, parentCategoryId: number) => {
    try {
      const params = { parentCategoryId };
      const { data } = await api().get(`/admin/users/${userId}/category-permissions`, { params });
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  // 전체 사용자 카테고리 권한 상태 조회
  // parentCategoryId 하위 기준으로 전체 사용자의 권한 상태(ALL/PARTIAL/NONE) 반환
  export const getCategoryPermissionStatus = async (parentCategoryId: number) => {
    try {
      const params = { parentCategoryId };
      const { data } = await api().get(`/admin/users/category-permission-status`, { params });
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  // 특정 카테고리에 권한이 있는 사용자 목록 조회
  export const getPermittedUsersForCategory = async (categoryId: number) => {
    try {
      const { data } = await api().get(`/admin/category/${categoryId}/permitted-users`);
      return data.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  // 사용자 카테고리 권한 추가
  export const addCategoryPermission = async (userId: number, categoryId: number) => {
    try {
      const baody = { userId, categoryId };
      const { data } = await api().post("/admin/users/category-permissions/add", baody);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  //사용자 카테고리 권한 제거
  export const removeCategoryPermission = async (userId: number, categoryId: number) => {
    try {
      const body = { userId, categoryId };
      const { data } = await api().post("/admin/users/category-permissions/remove", body);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  export const togglePermission = async (boardId: number, categoryPermissionEnabled: boolean) => {
    try {
      const body = { categoryPermissionEnabled };
      const { data } = await api().post(`/admin/board/${boardId}/category-permission`, body);
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  //사용자 별 게시물 조회 이력 (관리자용)
  export const viewPostLogs = async (
    title?: string, 
    userName?: string, 
    viewedAtFrom?: string, 
    viewdAtTo?: string, 
    page?: number, 
    size?: number
  ) => {
    try {
      const params = { title, userName, viewedAtFrom, viewdAtTo, page, size };
      const { data } = await api().get('/post/admin/view-logs', { params });
      return {
        content: data.data || [],
        total: data.total || 0,
      };
    } catch (e) {
      console.log(e);
      return { content: [], total: 0 };
    }
  };

  export const slideFileCheck = async () => {
    await api().post("/admin/temp/slide/file-check");
  };

  // export const updateUserEnglishName = async (userId: number, englishName: string) => {
  //   try {
  //     const body = { userId, englishName };
  //     const { data } = await api().post("/admin/user/english-name", body);
  //   } catch (e) {
  //     console.log(e);
  //     throw e;
  //   }
  // };